/* eslint-disable no-use-before-define */
import ax from 'axios';
import useAxios from '@use-hooks/axios';
import cleanDeep from 'clean-deep';
import queryString from 'query-string';
import { PORTAL_API, deployEnv } from './constants';
import { getToken, removeToken } from './helpers';
import { generatePageData, generateInitialPageData } from './generators'

function setupConfig() {
  const config = {
    baseURL: PORTAL_API,
    withCredentials: true,
  };

  const token = getToken();
  if (token) {
    config.headers = {
      Authorization: `Bearer ${token}`,
    };
  }
  return config;
}

// needs to be invoked before use get the updated token
// example: axiosWithAuth().get('/endpoint)
// this way setupConfig is invoked before the request and gets updated
export const axiosWithAuth = () => {
  const axios = ax.create(setupConfig())
  // Add a request interceptor
  axios.interceptors.response.use((response) => response,
    (error) => {
      if (error && error.response && error.response.status === 401) {
        logout()
      }
      return Promise.reject(error)
    }
  );
  return axios
};

// without auth header just use
export const axios = ax.create(setupConfig());

export const queryWarmup = (config = {}) => axiosWithAuth().get('/dashboard/current', { ...config });
export const queryStatus = (config = {}) => axiosWithAuth().get('/authentication/status', { ...config });
export const queryUserImage = (id, config = {}) => axiosWithAuth().get(`/person/${id}/photo`, {
  responseType: 'arraybuffer',
  ...config
});
export const queryUserInfo = (id, config = {}) => axiosWithAuth().get(`/person/${id}`, { ...config })
export const queryToken = (config = {}) => {
  // if local development add local parameter
  const isProd = process.env.NODE_ENV === 'production'
  const localQuery = isProd ? '' : '?local=true'
  return axios.get(`/authentication/login/token${localQuery}`, { ...config })
}
export const querySubwebs = (config = {}) => axiosWithAuth().get('/authentication/subwebs', { ...config })
export const querySubweb = (subweb, config = {}, isSeo = false) => {
  const queryObject = {
    seoInfo: isSeo ? true : null,
    ...config.params,
  }
  const params = cleanDeep(queryObject);
  return axiosWithAuth().get(`/subwebs/${subweb}`, { ...config, params })
}
export const querySidenavPagesByLang = (subweb, language = 'pt', config = {}) => {
  const queryObject = {
    language,
    ...config.params,
  }
  const params = cleanDeep(queryObject);
  return axiosWithAuth().get(`/subwebs/${subweb}/sidenav/pages`, { ...config, params })
}

export const querySubwebSidenav = (subweb, language = 'pt') => {
  const queryObject = {
    language
  }
  const params = cleanDeep(queryObject);
  return axiosWithAuth().get(
    `/subwebs/${subweb}/sidenav`,
    {
      params,
    }
  )
}

export const queryNews = (query = '', config = {}) => axiosWithAuth().get(`/news?${query}`, { ...config })

export const queryNewsCategories = (
  { language = 'pt' }, config = {}
) => {
  const queryObject = {
    language,
  }
  const params = cleanDeep(queryObject);
  return axiosWithAuth().get('/news/categories', { params, ...config })
}

export const queryNewsChannel = ({ language = 'pt', onlyOfficial = false } = {}, config = {}) => {
  const queryObject = {
    language,
    onlyOfficial,
  }
  const params = cleanDeep(queryObject);
  return axiosWithAuth().get('/events/channel', { params, ...config })
}

export const queryNewsTags = (
  { language = 'pt' }, config = {}
) => {
  const queryObject = {
    language,
  }
  const params = cleanDeep(queryObject);
  return axiosWithAuth().get('/news/tag', { params, ...config })
}

export const queryContests = (query = '', config = {}) => axiosWithAuth().get(`/contests/list?${query}`, { ...config })

export const queryContestFilters = (query = '', config = {}) => axiosWithAuth().get(`/contests/filters?${query}`, { ...config })

export const queryCalendar = (config = {}) => axiosWithAuth().get('/calendar', { ...config })

export const queryLocations = (config = {}) => axiosWithAuth().get('/locations', { ...config })

export const queryFileInfo = (query = '', config = {}) => axiosWithAuth().get(`/file/other/${query.id}`, { ...config })

export const updateSubwebSidenav = ({
  sidenavData,
  language,
  subweb,
}) => {
  // eslint-disable-next-line no-console
  if (typeof language !== 'string') console.error('Missing langauge parameter.')
  const sidenavSchema = {
    language,
    content: sidenavData
  }

  const body = {
    list: [sidenavSchema]
  }

  return axiosWithAuth()
    .put(
      `/subwebs/${subweb}/sidenav`,
      body,
    )
};

export const sendEmailAdmin = ({
  title,
  message,
}) => {
  const body = {
    message
  }
  const query = `subject=${title}`;
  return axiosWithAuth()
    .post(
      `/email/admin?${query}`,
      body,
    )
};

export const sendEmailAll = ({
  title,
  message,
}) => {
  const body = {
    message
  }
  const query = `subject=${title}`;
  return axiosWithAuth()
    .put(
      `/email/all?${query}`,
      body,
    )
};

export const sendEmailSome = ({
  message,
  query
}) => {
  const body = {
    message
  }
  return axiosWithAuth()
    .post(
      `/email/some?${query}`,
      body,
    )
};

export const uploadImage = ({
  file,
  language = [],
  keyword = [],
  collection,
  subweb,
  permissionLevel
}) => {
  const queryObject = {
    keyword,
    collection,
    subweb,
    permissionLevel
  };

  const fileData = {
    file,
    language: JSON.stringify(language),
  };

  const form = new FormData();
  form.append('file', fileData.file);
  form.append('language', fileData.language);

  // removes undefined, null, etc
  const params = cleanDeep(queryObject);
  const query = queryString.stringify(params, { encode: false });

  return axiosWithAuth().post(`/file/image?${query}`, form, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
};

export const updateImage = ({
  id,
  language = [],
  subweb,
  file,
  permissionLevel
}) => {
  const queryObject = {
    subweb,
    permissionLevel
  }
  const fileData = {
    file,
    language: JSON.stringify(language),
  };
  const form = new FormData();

  // removes undefined, null, etc
  const params = cleanDeep(queryObject);
  const query = queryString.stringify(params, { encode: false });

  if (file) {
    form.append('file', fileData.file);
  }
  form.append('language', fileData.language);

  return axiosWithAuth().patch(`/file/image/${id}?${query}`, form, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    }

  });
};

export const getImageCollections = (
  {
    subweb
  },
  config = {}
) => {
  const params = {
    subweb
  }
  const url = 'file/collections'
  return axiosWithAuth().get(url, { params, ...config })
}

export const getContent = (
  {
    pageId,
    subweb,
    language = 'pt',
    id = -1
  },
  config = {},
  isSeo = false
) => {
  const url = isSeo ? 'page/seo' : '/page'
  const queryObject = {
    path: pageId,
    subweb,
    language,
    id
  }

  const params = cleanDeep(queryObject);

  return axiosWithAuth().get(url, { params, ...config })
};

export const getSeo = (
  {
    pageId,
    lang = 'pt',
    subweb,
  },
  config = {}
) => {
  const params = {
    language: lang
  }
  const url = `subwebs/${subweb}/content/${pageId}/seo`
  return axiosWithAuth().get(url, { params, ...config })
}

export const getVersion = (
  {
    pageId,
    subweb,
    versionId,
    statusId,
  },
  config = {}
) => {
  const params = {
    status: statusId
  }
  const url = `subwebs/${subweb}/content/${pageId}/versions/${versionId}`
  return axiosWithAuth().get(url, { params, ...config })
}

export const getVersions = (
  {
    pageId,
    lang = 'pt',
    subweb,
  },
  config = {}
) => {
  const params = {
    language: lang
  }
  const url = `subwebs/${subweb}/content/${pageId}/versions`
  return axiosWithAuth().get(url, { params, ...config })
}

// States:
// None = 0,
// Draft = 1,
// Published = 2,
// Archive = 3,
// Deleted = 4
export const publishContent = ({
  pageId,
  pageData,
  language,
  state,
  subweb,
  slug = null,
  hardSave = false,
  preview = false
}) => {
  // adds logic before publish (such as removing SEO image on dynamic flag...)
  const pageDataComputed = generatePageData(pageData)

  const pageSchema = {
    language,
    state,
    content: pageDataComputed,
    slug,
  }

  const pageSchemaClean = pageSchema

  const body = {
    list: [pageSchemaClean]
  }

  // commit is true in the QA and Production environments
  const queryObject = {
    id: pageId,
    hardSave,
  }
  const params = cleanDeep(queryObject);
  const query = queryString.stringify(params, { encode: false });
  return preview ? axiosWithAuth()
    .post(
      `/subwebs/${subweb}/content/${pageId}/preview?${query}`,
      body,
    ) : axiosWithAuth()
    .put(
      `/subwebs/${subweb}/content/${pageId}?${query}`,
      body,
    )
};

export const createPage = ({
  title = '',
  slug = '',
  language = 'pt',
  subweb,
}) => {
  // adds logic before publish (such as removing SEO image on dynamic flag...)
  const pageDataComputed = generateInitialPageData({ title })
  // saved as draft
  const pageSchema = {
    language,
    state: 1,
    content: pageDataComputed,
    slug,
  }

  const body = {
    list: [pageSchema]
  }

  return axiosWithAuth()
    .post(
      `/subwebs/${subweb}/content`,
      body,
    )
};

export const duplicatePage = ({
  subweb,
  slug,
  title,
  pageLiveData
}) => {

  // adds logic before publish (such as removing SEO image on dynamic flag...)
  const pageDataComputed = generateInitialPageData({ title })

  if (Array.isArray(pageLiveData)) {
    pageLiveData.forEach(element => {
      pageDataComputed.push(element)
    });
  }
  
  // saved as draft
  const pageSchema = {
    language: 'pt',
    state: 1,
    content: pageDataComputed,
    slug,
  }
  const body = {
    list: [pageSchema]
  }

  return axiosWithAuth()
    .post(
      `/subwebs/${subweb}/content`,
      body,
    )
}

export const deletePage = ({
  subweb,
  pageId,
}) => axiosWithAuth()
  .delete(`/subwebs/${subweb}/content/${pageId}`);

export const deleteSubweb = ({
  subweb
}) => axiosWithAuth()
  .delete(`/subwebs/${subweb}`);


// state 3 is 'Archived'
export const archivePage = ({
  subweb,
  pageId,
  language,
  allLanguages = false,
}) => {
  const queryObject = {
    language,
    allLanguages,
  }
  const params = cleanDeep(queryObject);
  const query = queryString.stringify(params, { encode: false });
  return axiosWithAuth()
    .put(`/subwebs/${subweb}/content/${pageId}/state/3?${query}`)
};

export const SLUG_KEY = 'slug'
export const putSlug = (
  {
    pageId,
    language,
    subweb,
    slug,
  }
) => {
  const params = {
    language
  }

  if (!pageId || !language || !subweb || !slug || !typeof slug === 'string') {
    // eslint-disable-next-line no-console
    console.error('Invalid parameters for request: putSlug')
  }

  const query = queryString.stringify(params, { encode: false });

  return axiosWithAuth()
    .put(`/subwebs/${subweb}/content/${pageId}/slug/${slug}?${query}`)
};

export const FILES_KEY = 'files'
export const getFiles = (
  key,
  {
    lang = 'pt',
    subweb,
    currentPage,
    recordsPerPage,
    search,
    onlyAddedByMe,
    alsoAddedByMe
  },
  config = {}
) => {
  const queryObj = {
    language: lang,
    subweb,
    currentPage,
    recordsPerPage,
    search,
    onlyAddedByMe,
    alsoAddedByMe,
    orderby: 'lastModifiedDate',
    sort: 'desc'
  }
  const params = cleanDeep(queryObj);
  const url = 'file/other'
  return axiosWithAuth().get(url, { params, ...config })
}

export const getFileById = (
  key,
  {
    fileId
  },
  config = {}
) => axiosWithAuth().get(`/file/other/${fileId}`, config)

export const uploadFile = ({
  file,
  language = 'pt',
  slug,
  title,
  description,
  subweb,
  expiresAt,
}) => {
  const queryObject = {
    slug,
    language,
    subweb,
    title,
    description,
    expiresAt,
  };

  const fileData = {
    file,
  };

  const form = new FormData();
  form.append('file', fileData.file);

  // removes undefined, null, etc
  const params = cleanDeep(queryObject);
  const query = queryString.stringify(params, { encode: false });

  return axiosWithAuth().post(`/file/other?${query}`, form, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
};

export const putFile = ({
  fileId,
  subweb,
  language = 'pt',
  file,
  title,
  description,
  expiresAt,
}) => {
  const queryObject = {
    fileId,
    subweb,
    language,
    title,
    description,
    expiresAt,
  };

  const fileData = {
    file,
  };

  const form = new FormData();
  if (file) form.append('file', fileData.file);

  // removes undefined, null, etc
  const params = cleanDeep(queryObject);
  const query = queryString.stringify(params, { encode: false });

  return axiosWithAuth().put(`/file/other?${query}`, form, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
};

export const deleteFiles = ({
  fileId,
  subweb
}) => axiosWithAuth().delete(`file/other/${fileId}?subweb=${subweb}`)

export const deleteImage = ({
  fileId,
  subweb
}) => axiosWithAuth().delete(`file/image/${fileId}?subweb=${subweb}`)

export const ALL_PEOPLE_KEY = 'allPeople'
export const getAllPeople = (
  key,
  {
    lang = 'pt',
    offset,
    recordsPerPage,
    search,
  },
  config = {}
) => {
  const queryObj = {
    language: lang,
    offset,
    recordsPerPage,
    searchString: search,
  }
  const params = cleanDeep(queryObj);
  return axiosWithAuth().get('/person', { params, ...config })
}

export const ALL_SUBWEB_PEOPLE = 'allSubwebPeople'
export const getSubwebPeople = (
  key,
  {
    lang = 'pt',
    subweb,
    currentPage,
    offset,
    recordsPerPage,
    search,
  },
  config = {}
) => {
  const queryObj = {
    language: lang,
    currentPage,
    offset,
    recordsPerPage,
    searchString: search,
  }
  const params = cleanDeep(queryObj);
  return axiosWithAuth().get(`subwebs/${subweb}/person/all`, { params, ...config })
}

export const PEOPLE_KEY = 'people'
export const getPeople = (
  key,
  {
    lang = 'pt',
    subweb,
    currentPage,
    recordsPerPage,
    search,
  },
  config = {}
) => {
  const queryObj = {
    language: lang,
    subweb,
    currentPage,
    recordsPerPage,
    searchString: search,
  }
  const params = cleanDeep(queryObj);
  return axiosWithAuth().get('/subwebs/people', { params, ...config })
}

export const getPeopleById = (
  key,
  {
    subweb,
    id,
  },
  config = {}
) => axiosWithAuth().get(`/subwebs/${subweb}/person/${id}`, config)

export const putPeopleById = (
  {
    subweb,
    id,
    cabinet,
    externalPhone,
    internalPhone,
    jobPt,
    jobEn,
    visible,
  },
  config = {}
) => {
  const queryObj = {
    cabinet,
    externalPhone,
    internalPhone,
    visible,
  }
  const bodyDirty = {
    jobPt,
    jobEn
  }
  const params = cleanDeep(queryObj);
  const query = queryString.stringify(params);
  const body = cleanDeep(bodyDirty);
  return axiosWithAuth().put(`/subwebs/${subweb}/person/${id}?${query}`, body, config)
}

export const SUBWEB_TYPE_KEY = 'subweb-type'
export const getSubwebTypes = (
  key,
  config = {}
) => axiosWithAuth().get('/subwebs/types', config)

export const SUBWEB_KEY = 'subweb'
export const postSubweb = (
  {
    slug,
    namePt,
    nameEn,
    description,
    active,
    typeId,
    uaOnlineId,
    serviceGuruId,
    serviceRHid,
    analyticsId,
    initials,
    logoPtTop,
    logoPtBottom,
    logoEnTop,
    logoEnBottom
  },
  config = {}
) => {
  const bodyDirty = {
    slug,
    namePt,
    nameEn,
    description,
    active,
    typeId,
    uaOnlineId,
    serviceGuruId,
    serviceRHid,
    analyticsId,
    initials,
    logoPtTop,
    logoPtBottom,
    logoEnTop,
    logoEnBottom
  }
  const body = cleanDeep(bodyDirty);

  return axiosWithAuth().post('/subwebs', body, config)
}

export const getSubwebInfo = (
  key,
  {
    subweb,
  },
  config = {}
) => axiosWithAuth().get(`/subwebs/${subweb}/info`, config)

export const putSubweb = (
  {
    subweb,
    slug,
    namePt,
    nameEn,
    description,
    active,
    typeId,
    uaOnlineId,
    serviceGuruId,
    serviceRHid,
    analyticsId,
    initials,
    logoPtTop,
    logoPtBottom,
    logoEnTop,
    logoEnBottom
  },
  config = {}
) => {
  const bodyDirty = {
    slug,
    namePt,
    nameEn,
    description,
    active,
    typeId,
    uaOnlineId,
    serviceGuruId,
    serviceRHid,
    analyticsId,
    initials,
    logoPtTop,
    logoPtBottom,
    logoEnTop,
    logoEnBottom
  }
  const body = cleanDeep(bodyDirty);

  return axiosWithAuth().put(`/subwebs/${subweb}`, body, config)
}

// user permissions
export const SUBWEB_USERS_KEY = 'subweb-users'
export const SUBWEB_USERS_ALL_KEY = 'subweb-users-all'
export const SUBWEB_USERS_PAGE_KEY = 'subweb-users-page'
export const getSubwebUsers = (
  key,
  {
    lang = 'pt',
    subweb,
    currentPage,
    recordsPerPage,
    search,
  },
  config = {}
) => {
  const queryObj = {
    language: lang,
    currentPage,
    recordsPerPage,
    searchString: search,
  }
  const params = cleanDeep(queryObj);
  return axiosWithAuth().get(`/subwebs/${subweb}/users`, { params, ...config })
}

export const getAllUsers = (
  key,
  {
    lang = 'pt',
    subweb, // will exclude all users from this subweb
    currentPage,
    recordsPerPage,
    search,
    filter, // 'service' or 'page'
    pageId, // if filter set to page, pass the pageId
  },
  config = {}
) => {
  const queryObj = {
    language: lang,
    currentPage,
    recordsPerPage,
    searchString: search,
    filter,
    pageId,
  }
  const params = cleanDeep(queryObj);
  return axiosWithAuth().get(`/subwebs/${subweb}/users/all`, { params, ...config })
}

export const postSubwebUser = ({
  subweb,
  userId,
}) => axiosWithAuth()
  .post(`/subwebs/${subweb}/user/${userId}`);

export const deleteSubwebUser = ({
  subweb,
  userId,
}) => axiosWithAuth()
  .delete(`/subwebs/${subweb}/user/${userId}`);

export const getSubwebPageUsers = (
  key,
  {
    lang = 'pt',
    subweb,
    pageId,
    currentPage,
    recordsPerPage,
    search,
  },
  config = {}
) => {
  const queryObj = {
    language: lang,
    currentPage,
    recordsPerPage,
    searchString: search,
  }
  const params = cleanDeep(queryObj);
  return axiosWithAuth().get(`/subwebs/${subweb}/content/${pageId}/users`, { params, ...config })
}

export const postSubwebPageUser = ({
  subweb,
  pageId,
  userId,
}) => axiosWithAuth()
  .post(`/subwebs/${subweb}/content/${pageId}/user/${userId}`);

export const deleteSubwebPageUser = ({
  subweb,
  pageId,
  userId,
}) => axiosWithAuth()
  .delete(`/subwebs/${subweb}/content/${pageId}/user/${userId}`);


// hooks
export const useQueryContent = ({
  subweb, language = 'pt', id = -1
}) => {
  const url = `subwebs/${subweb}/content/${id}`
  const queryObject = {
    language,
  }

  const params = cleanDeep(queryObject);

  return useAxios({
    axios: axiosWithAuth(),
    url,
    options: {
      params
    },
  })
};

export const getPageFromSlug = (
  {
    slug,
    lang = 'pt',
    subweb,
  },
  config = {}
) => {
  const params = {
    language: lang
  }
  const url = `subwebs/${subweb}/content/slug/${slug}`
  return axiosWithAuth().get(url, { params, ...config })
}

export const getPageFromSlugOnSupport = (
  {
    slug,
    lang = 'pt',
    subweb,
    publishedVersion = true
  },
  config = {}
) => {
  const params = {
    language: lang,
    publishedVersion
  }
  const url = `subwebs/${subweb}/content/slug/${slug}`
  return axiosWithAuth().get(url, { params, ...config })
}

export const IMAGES_KEY = 'images'
export const getImages = (
  key,
  {
    currentPage,
    recordsPerPage,
    listAvailableSizes,
    addedByMe,
    search,
    language = 'pt',
    collection,
    subweb,
    pagination = true,
    onlyEditable
  },
  opts
) => {
  const queryObject = {
    recordsPerPage,
    currentPage,
    search,
    language,
    listAvailableSizes,
    addedByMe,
    collection,
    subweb,
    pagination,
    onlyEditable
  };
  // removes undefined, null, etc
  const params = cleanDeep(queryObject);
  return axiosWithAuth().get('/file/image', { params, ...opts })
};

export const useQueryImages = (
  {
    currentPage,
    recordsPerPage,
    listAvailableSizes,
    addedByMe,
    search,
    language = 'pt',
    collection,
    subweb,
    pagination = true,
  },
  opts
) => {
  const queryObject = {
    recordsPerPage,
    currentPage,
    search,
    language,
    listAvailableSizes,
    addedByMe,
    collection,
    subweb,
    pagination,
  };
  // removes undefined, null, etc
  const params = cleanDeep(queryObject);

  return useAxios({
    axios: axiosWithAuth(),
    url: '/file/image',
    options: {
      params,
    },
    ...opts,
  });
};

export const useQueryImage = (
  {
    id,
    height,
    language = 'pt',
    subweb
  }, opts) => {
  const queryObject = {
    height,
    language,
    subweb,
  };
  // removes undefined, null, etc
  const params = cleanDeep(queryObject);

  return useAxios({
    axios: axiosWithAuth(),
    url: `/file/image/${id}`,
    trigger: id,
    options: {
      params,
    },
    ...opts,
  });
};

export const logout = async () => {
  // logout request
  const token = getToken()
  if (token) {
    removeToken()
    window.location = `${PORTAL_API}/authentication/logout`;
  }
};

export const logError = ({
  code = null,
  page = null,
  info = 'Missing more information',
}, config = {}) => {
  const app = `editor.${deployEnv}`
  const queryObject = {
    code,
    page,
    info,
    app,
  }
  const params = cleanDeep(queryObject);
  return axios.post('/teams/message', { params, ...config })
}

export const getCourseInfo = (
  {
    lang = 'pt',
  },
  config = {}
) => {
  const queryObj = {
    language: lang,
  }
  const params = cleanDeep(queryObj);
  return axiosWithAuth().get('/courses/info', { params, ...config })
}


export function getDepartmentPeople({
  lang = 'pt',
  department = '',
  type = '',
  ...props
}) {
  try {
    const hasType = type ? `&type=${type}` : '';
    const token = getToken()
    return axiosWithAuth().get(
      `/subwebs/${department}/people?language=${lang}${hasType}`,
      {
        lang,
        redirectErrors: false,
        options: {
          ...token
        },

        ...props
      }
    );
  } catch (error) {
    throw error;
  }
}

export function getDepartmentManagement({
  lang = 'pt',
  department = '',
  ...props
}) {
  try {
    const token = getToken()
    return axiosWithAuth().get(
      `/subwebs/${department}/management?language=${lang}`,
      {
        lang,
        redirectErrors: false,
        options: {
          ...token
        },
        ...props
      }
    );
  } catch (error) {
    throw error;
  }
}

export function getPeopleTabs({
  lang = 'pt',
  department = '',
  type = '',
  ...props
}) {
  try {
    const hasType = type ? `&type=${type}` : '';
    const token = getToken()
    return axiosWithAuth().get(
      `/subwebs/${department}/people/types?language=${lang}`,
      {
        lang,
        redirectErrors: false,
        options: {
          ...token
        },

        ...props
      }
    );
  } catch (error) {
    throw error;
  }
}

export const EXPORT_EXCEL_KEY = 'export_excel'
export function getPivotsExcel() {
  try {
    return axiosWithAuth().get('/dashboard/pivotslist', {
      responseType: 'arraybuffer', // Important: Set the response type to arraybuffer for binary data
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' // Set appropriate headers if required
      }
    }, {});
  } catch (error) {
    throw error;
  }
}

export const EXPORT_STATUS_KEY = 'export_stats'
export function getStatsExcel(intervalForm) {

  const queryParams = `${intervalForm.startDate && intervalForm.endDate ? `startDate=${intervalForm.startDate}&endDate=${intervalForm.endDate}` : ''}`;

  try {
    return axiosWithAuth().get(`/dashboard/stats?${queryParams}`, {
      responseType: 'arraybuffer', // Important: Set the response type to arraybuffer for binary data
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' // Set appropriate headers if required
      }
    });
  } catch (error) {
    throw error;
  }
}
